/* eslint-disable max-lines-per-function */
import './Global.scss';
import '@mvps-genesis/fasten-header';
import {PageManager} from '@mvps-genesis/core';
import {decodeEntities} from '@mvps-genesis/utilities';
import dynamicFormElementAttributes from '@mvps-genesis/dynamic-form-element-attributes';
import 'whatwg-fetch';

export default class Global extends PageManager {

	constructor (pageContext) {

		super(pageContext);

		this.mivaJS = window.mivaJS || pageContext;

		dynamicFormElementAttributes();
	}

	onReady () {

		/**
		 * Initialize LazyLoading
		 */
		(async () => {
			// Elements
			const lazyLoadElements = document.getElementsByClassName('lazyload');

			if (lazyLoadElements.length === 0) {
				return;
			}

			window.lazySizesConfig = window.lazySizesConfig || {};
			window.lazySizesConfig.expand = 200;

			document.addEventListener('lazybeforeunveil', (e) => {
				if (e.target && e.target.nodeName === 'IFRAME') {
					e.target.src = e.target.getAttribute('data-src');
				}
			});

			// Module
			await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
			await import('lazysizes/plugins/bgset/ls.bgset');
			await import('lazysizes/plugins/custommedia/ls.custommedia');
			await import('lazysizes');

		})();

		this.initSkipToSection();

		(async () => {
			await import('@mvps-genesis/search-preview');
		})();

		(async () => {
			await import('extensions/global/hideShowCattree');
		})();

		this.initProductCompare();

		/**
		 * Initialize Accordion Polyfill
		 */
		(async () => {
			// Elements
			const accordionElements = document.getElementsByClassName('x-accordion');

			if (accordionElements.length === 0) {
				return;
			}

			// Module
			await import('details-polyfill');
		})();

		(async () => {
			await import('extensions/global/printDialog');
		})();

		this.reCAPTCHAForms();
		this.initContinueShopping();
		this.initGlobalMessageContainer();
		this.initMiniBasket();
		this.initModals();
		this.initGTM();

		(async () => {
			const {default: NavigationMenu} = await import('extensions/navigation-menu');

			/**
			 * Initialize the Drop-Down Navigation extension
			 */
			new NavigationMenu({
				drawers: '.js-navigation-menu__drawer',
				mainMenu: '.js-navigation-menu',
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous'
			});
		})();

		(async () => {

			/**
			 * Initialize carousels
			 */

			const {initCarousels} = await import('@mvps-genesis/carousel');
			initCarousels();
		})();

		const carouselElements = document.getElementsByClassName('js-carousel');

		if (carouselElements.length) {
			for (const carouselElement of carouselElements) {
				const options = JSON.parse(decodeEntities(carouselElement.getAttribute('data-options')));
				const carouselSlides = carouselElement.querySelector('.x-carousel__slides');
				const carouselControls = carouselElement.querySelector('.x-carousel__arrows');

				if (options && options.perView && carouselSlides && carouselControls) {
					const slideCount = carouselSlides.childElementCount;

					if (slideCount > options.perView) {
						carouselControls.classList.add('u-shown--l');
					}
				}

			}
		}

		/**
		 * Initialize Inline Form Validations
		 */
		(async () => {
			const forms = document.querySelectorAll('.js-form-validator');

			if (forms.length) {
				const {default: HTMLFormValidator} = await import('extensions/html-form-validator/HTMLFormValidator');

				for (const form of forms) {
					form.HTMLFormValidator = new HTMLFormValidator({
						formElement: form,
						wrapperSelector: '.c-form-list__item'
					});
				}
			}
		})();

		this.initFacets();

		// Add visual focus state to filtering select boxes
		const filteringItems = document.querySelectorAll('.x-display-list-filtering__item select');

		if (filteringItems) {
			for (const filterItem of filteringItems) {
				const filterItemContainer = filterItem.closest('.x-display-list-filtering__item');

				if (filterItemContainer) {
					filterItem.addEventListener('focus', () => {
						filterItemContainer.classList.add('is-focused');
					});

					filterItem.addEventListener('blur', () => {
						filterItemContainer.classList.remove('is-focused');
					});
				}
			}
		}

		// Load rewards points modal content
		if (this.mivaJS && this.mivaJS.Points_Url) {
			this.initPointsModals(this.mivaJS.Points_Url);
		}
	}

	async initSkipToSection () {
		const sections = [];

		const menuElem = document.querySelector('.js-navigation-menu');

		if (menuElem) {
			sections.push({
				label: 'Menu',
				targetElement: menuElem
			});
		}

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const mainContentElem = document.querySelector('.t-main-content-element');

		if (mainContentElem) {
			sections.push({
				label: 'Content',
				targetElement: mainContentElem
			});
		}

		const facetsElem = document.getElementById('js-faceting');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		const footerElem = document.querySelector('.t-site-footer');

		if (footerElem) {
			sections.push({
				label: 'Footer',
				targetElement: footerElem
			});
		}

		if (!sections.length) {
			return;
		}

		const {SkipToSection} = await import('extensions/accessibility');

		SkipToSection.createFromArray(sections);
	}

	async reCAPTCHAForms () {
		const captchaForms = document.getElementsByClassName('js-form-captcha');

		if (!captchaForms.length) {
			return;
		}

		const {reCAPTCHAForms} = await import('extensions/global/reCAPTCHAForms');

		// eslint-disable-next-line new-cap
		new reCAPTCHAForms(captchaForms, this.pageContext);
	}

	async initContinueShopping () {
		const {default: ContinueShopping} = await import('@mvps-genesis/continue-shopping');

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
	}

	async initGlobalMessageContainer () {
		const {GlobalMessageContainer} = await import('extensions/messages');

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});
	}

	async initMiniBasket () {

		/**
		 * Initialize the Mini-Basket extension
		 */

		const {default: MiniBasket} = await import('extensions/mini-basket/MiniBasket');

		new MiniBasket({
			openOnAddToCart: true
		});
	}

	async initModals () {
		const modalTriggerAttribute = 'data-micromodal-trigger';

		// Collect all micromodal elements
		this.modalTriggerElements = document.querySelectorAll(`[${modalTriggerAttribute}]`);

		if (!this.modalTriggerElements.length) {
			return;
		}

		// Import micromodal
		const {default: MicroModal} = await import('micromodal');

		// There are issues with the same micromodal trigger, with different elements.
		this.modalTriggerElements.forEach((element, index) => {
			element.addEventListener('click', (event) => {
				event.preventDefault();
			});

			const triggerValue = element.getAttribute(modalTriggerAttribute);
			const newTriggerValue = `${modalTriggerAttribute}-${index}`;

			element.removeAttribute(modalTriggerAttribute);
			element.setAttribute('data-modal-target', triggerValue);
			element.setAttribute(newTriggerValue, triggerValue);

			if (!element.hasAttribute('aria-haspopup')) {
				element.setAttribute('aria-haspopup', 'dialog');
			}

			let options = {
				awaitCloseAnimation: true,
				awaitOpenAnimation: true,
				disableScroll: true,
				onClose: () => {
					const elements = document.querySelectorAll('[data-modal-aria-hidden]');
					for (const el of elements) {
						el.removeAttribute('aria-hidden');
						el.removeAttribute('data-modal-aria-hidden');
					}
				},
				onShow: (modal) => {
					let el = modal;
					let node = null;
					const nodes = [];

					do {
						var parent = el.parentNode;
						// Collect element children
						for (var i = 0, iLen = parent.childNodes.length; i < iLen; i++) {
							node = parent.childNodes[i];
							// Collect only sibling nodes that are elements and not the current element
							if (node.nodeType === 1 && node !== el) {
								nodes.push(node);
							}
						}
						// Go up to parent
						el = parent;
					// Stop when processed the body's child nodes
					} while (el.tagName.toLowerCase() !== 'body');

					for (const n of nodes) {
						if (n.getAttribute('aria-hidden') === 'true') {
							continue;
						}

						n.setAttribute('aria-hidden', true);
						n.setAttribute('data-modal-aria-hidden', 1);
					}
				},
				openTrigger: newTriggerValue
			};

			if (triggerValue === 'js-global-account') {
				options.disableScroll = false;
			}

			MicroModal.init(options);
		});

		this.bindModalCloseEvents();
	}

	async initFacets () {

		/**
		 * Initialize Facets Functionality
		 */
		const faceting = document.getElementById('js-faceting');

		if (faceting) {
			const {default: Facets} = await import('extensions/facets/Facets');
			new Facets();
		}
	}

	async initProductCompare () {

		/**
		 * Initialize Product Compare
		 */
		const prodCmpContainer = document.getElementById('js-compare-container');
		const prodCmpSelElements = document.getElementsByClassName('js-compare-select-container');

		if (prodCmpContainer && prodCmpSelElements.length && typeof this.pageContext.Json_Api_Url === 'string') {
			for (const cmpElement of prodCmpSelElements) {
				cmpElement.classList.remove('u-hidden');
			}

			prodCmpContainer.classList.remove('u-hidden');

			const {default: ProductCompare} = await import('extensions/product-compare/ProductCompare');

			new ProductCompare({url: this.pageContext.Json_Api_Url});
		}
	}

	initPointsModals (pointsUrl) {
		const pointsModals = document.getElementsByClassName('js-rewards-points-content');

		if (!pointsModals.length) {
			return false;
		}

		(async () => {
			const response = await fetch(pointsUrl);

			if (!response.ok) {
				throw new Error('Network response was not successful');
			}

			const pointsDoc = await response.text();
			const doc = new DOMParser().parseFromString(pointsDoc, 'text/html');
			const pointsContent = doc.getElementById('js-points-content');

			if (pointsContent) {
				for (const modalContent of pointsModals) {
					modalContent.appendChild(pointsContent);
				}
			}
		})();

		return true;
	}

	bindModalCloseEvents () {
		if (!this.modalTriggerElements || !this.modalTriggerElements.length) {
			return;
		}

		const modalId = 'js-global-account';
		const modalElement = document.getElementById(modalId);

		if (!modalElement) {
			return;
		}

		const triggerElements = [];

		for (const trigger of this.modalTriggerElements) {
			if (trigger.getAttribute('data-modal-target') === modalId) {
				triggerElements.push(trigger);
			}
		}

		if (!triggerElements.length) {
			return;
		}

		const openClass = 'is-open';

		document.addEventListener('mousedown', (event) => {
			event.stopPropagation();

			let closeModal = true;

			if (modalElement.contains(event.target) || modalElement === event.target) {
				closeModal = false;
			} else {
				triggerElements.some((element) => {
					if (element.contains(event.target) || element === event.target) {
						closeModal = false;

						return true;
					}

					return false;
				});
			}

			if (closeModal && modalElement.classList.contains(openClass)) {
				const closeTrigger = modalElement.querySelector('[data-micromodal-close]');

				if (closeTrigger) {
					closeTrigger.click();
				}
			}
		});
	}

	async initGTM () {

		const {default: GTM} = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}
}
